import React, { useMemo, useRef, useCallback } from 'react';

import { useProcessAsync } from '@gogame/hooks-ts';

import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/constants';
import {
  useConcept,
  useConceptOriginal,
} from 'scenes/Gallery/GalleryPage/Concepts/data';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/constants';
import {
  useCreate,
  useIsUpdating,
} from 'scenes/Gallery/GalleryPage/Creatives/data';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';

import CreateCreativesForm from './CreateCreativesForm';
import { getValidationSchema } from './schemas';

const CreateCreatives = ({
  initialValues = { [CREATIVE]: [] },
  onSubmit,
  onClose,
  onExited,
  open,
}) => {
  const [loading, processAsync] = useProcessAsync();

  const promiseRef = useRef([]);

  const handleProcessAsync = useCallback((p) => {
    promiseRef.current = [...promiseRef.current, p];
    return () => {
      promiseRef.current = promiseRef.current.filter((c) => c !== p);
    };
  }, []);

  const [createCreatives] = useCreate();

  const isUpdating = useIsUpdating();

  const {
    isAllowBusinessPartner,
    isAllowRequestedBy,
    isAllowJiraTask,
    isAllowDesigner,
    isAllowOutsourceStudio,
    isAllowTested,
    isAllowProductionType,
  } = useGalleryPermissions();

  const { data: concept, isLoading: conceptLoading } = useConcept({
    id: initialValues[CREATIVE.CONCEPT_ID],
  });

  const { data: original, isLoading: originalLoading } = useConceptOriginal({
    id: initialValues[CREATIVE.CONCEPT_ID],
  });

  const handleSubmit = async ({
    [CREATIVE.CREATIVES]: creatives,
    [CREATIVE.CONCEPT_ID]: conceptId,
  }) => {
    const fileGuids = await Promise.all(
      promiseRef.current.map((p) => processAsync(p))
    );

    await createCreatives({
      creatives: creatives.map((creative, index) => ({
        ...creative,
        [CREATIVE.FILE_GUID]: fileGuids[index],
      })),
      conceptId,
    });
    onSubmit();
  };

  const initial = useMemo(() => {
    if (!concept || !original) {
      return initialValues;
    }
    let initialData = {
      ...initialValues,
    };

    if (isAllowDesigner) {
      initialData[CREATIVE.DESIGNER] = original[CREATIVE.DESIGNER];
    }

    if (isAllowOutsourceStudio) {
      initialData[CREATIVE.OUTSOURCE_STUDIO_ID] =
        original[CREATIVE.OUTSOURCE_STUDIO_ID];
    }

    initialData[CREATIVE.CREATIVES] = initialValues[CREATIVE.CREATIVES].map(
      (creative) => {
        let creativeResult = {
          ...creative,
          [CREATIVE.INHERIT_TAGS]: concept[CONCEPT.TAGS],
          [CREATIVE.NETWORKS]: original[CREATIVE.NETWORKS],
          [CREATIVE.LANGUAGE]: original[CREATIVE.LANGUAGE],
          [CREATIVE.CHANNELS]: original[CREATIVE.CHANNELS],
          [CREATIVE.COLOR]: original[CREATIVE.COLOR],
          [CREATIVE.OS]: original[CREATIVE.OS],
          [CREATIVE.ENHANCERS]: original[CREATIVE.ENHANCERS],
          [CREATIVE.APPLICATION]:
            original[CREATIVE.APPLICATION] ||
            concept[CONCEPT.APPLICATIONS]?.[0],
          [CREATIVE.AI_GENERATED]: original[CREATIVE.AI_GENERATED],
        };
        creativeResult[CREATIVE.DESCRIPTION] =
          `Original File Name: ${creative.file?.name}`;

        if (isAllowJiraTask) {
          creativeResult[CREATIVE.JIRA_TASK] = original[CREATIVE.JIRA_TASK];
        }
        if (isAllowDesigner) {
          creativeResult[CREATIVE.DESIGNER] = original[CREATIVE.DESIGNER];
        }
        if (isAllowOutsourceStudio) {
          creativeResult[CREATIVE.OUTSOURCE_STUDIO_ID] =
            original[CREATIVE.OUTSOURCE_STUDIO_ID];
        }
        if (isAllowRequestedBy) {
          creativeResult[CREATIVE.REQUESTED_BY] =
            original[CREATIVE.REQUESTED_BY];
        }
        if (isAllowBusinessPartner) {
          creativeResult[CREATIVE.BUSINESS_PARTNERS] =
            original[CREATIVE.BUSINESS_PARTNERS];
        }

        if (isAllowTested) {
          creativeResult[CREATIVE.TESTED] = original[CREATIVE.TESTED];
        }

        if (isAllowProductionType) {
          creativeResult[CREATIVE.PRODUCTION_TYPE] =
            original[CREATIVE.PRODUCTION_TYPE];
        }

        return creativeResult;
      }
    );

    return initialData;
  }, [
    initialValues,
    concept,
    original,
    isAllowBusinessPartner,
    isAllowRequestedBy,
    isAllowJiraTask,
    isAllowDesigner,
  ]);

  return (
    <CreateCreativesForm
      validationSchema={getValidationSchema({
        isAllowBusinessPartner,
        isAllowRequestedBy,
        isAllowJiraTask,
        isAllowDesigner,
        isAllowTested,
        isAllowProductionType,
      })}
      loading={loading || conceptLoading || originalLoading || isUpdating}
      onClose={onClose}
      active={!!concept && !!original}
      initialValues={initial}
      concept={concept}
      original={original}
      title={'Creatives Uploader'}
      subTitle={`Add Variants to «${concept?.name}» Concept`}
      onSubmit={handleSubmit}
      processAsync={handleProcessAsync}
      onExited={onExited}
      open={open}
    />
  );
};

export default CreateCreatives;
